// Unpublished Work © 2026 Deere & Company.
import React from 'react';
import {GRID_REORDER_COL_DEF, Stack, Typography} from '@deere/isg.component-library';
import moment from 'moment';
import {JOB} from 'OnLabor/workboard/constants/workboard-creation-flows';
import {getOperatorFormattedName} from 'OnLabor/workboard/utils/operator-utils';
import {IconDotFilled} from '@deere/icons';
import equipmentStatus from 'Common/constants/equipment-status';

const ICON_STYLE = {
    style: {
        fontSize: '12px'
    }
};
const NONE_COLOR = 'none';
const NOT_AVAILABLE_COLOR = '#5E5E5D';

export function getRows(jobs, operators, groupBy) {
    if (groupBy === JOB) {
        return jobs.map((job, index) => {
            return {
                [GRID_REORDER_COL_DEF.field]: job.title,
                priority: index + 1,
                id: job.id ?? job.jobId,
                jobs: [job],
                jobName: job.title,
                operators,
                operatorCount: job.operatorCount ?? 0,
                note: '',
                equipment: []
            };
        });
    }
    return operators.map((operator, index) => {
        const operatorName = getOperatorFormattedName(operator);

        return {
            [GRID_REORDER_COL_DEF.field]: operatorName,
            priority: index + 1,
            id: operator.id,
            jobs,
            operatorName,
            operators,
            note: '',
            equipment: []
        };
    });
}

export function renderRowsByOrder(baseRows, rows, setRows, rowOrder, selectRowOrder, setLoading) {
    setLoading(true);
    const rowsById = new Map(baseRows.map((r) => [r.id, r]));

    const prevOrder = rowOrder.length > 0 ? rowOrder : rows.map((r) => r.id);

    const preserved = prevOrder.filter((id) => rowsById.has(id));

    const baseIds = baseRows.map((r) => r.id);
    const additions = baseIds.filter((id) => !preserved.includes(id));

    const nextOrder = [...preserved, ...additions];

    const nextRows = nextOrder.map((id, i) => ({
        ...rowsById.get(id),
        priority: i + 1
    }));

    const orderChanged =
        nextOrder.length !== rowOrder.length ||
        nextOrder.some((id, i) => id !== rowOrder[i]);

    if (orderChanged) {
        selectRowOrder(nextOrder);
    }

    setRows(nextRows);
    setLoading(false);
}

export function getAssignedOperators(multiSelectItems) {
    const operatorIds = Object.values(multiSelectItems).flat();

    return operatorIds.length;
}

export function getAssignedJobs(multiSelectItems) {
    const jobIds = Object.values(multiSelectItems).flat();

    return jobIds.length;
}

export function getTotalOperatorCount(rows) {
    let count = 0;

    rows.forEach((row) => {
        count += row.operatorCount || 0;
    });
    return count;
}

export function getFormattedDateAndTime(details) {
    const workboardDate = moment(details.date, 'YYYY-MM-DD HH:mm:ss');
    const formattedDate = workboardDate.format('MMM DD, YYYY');
    const formattedTime = workboardDate.format('h:mm A');

    return {
        formattedDate,
        formattedTime
    };
}

export function getJobsAssignedToOperator(operatorId, jobs, values) {
    if (!jobs?.length || !values) {
        return '';
    }
    const names = [];

    for (const job of jobs) {
        const operators = values[job.id];

        if (operators?.includes(operatorId) && job?.title) {
            names.push(job.title);
        }
    }
    return names.join(', ');
}

export function getOperatorCountForJob(jobId, operatorCount, values, OPERATORS) {
    if (!jobId || !values || !operatorCount) {
        return '---';
    }

    const assigned = Object.values(values)
        .filter((jobIds) => jobIds && jobIds.some((id) => id === jobId))
        .length;

    return `${assigned}/${operatorCount} ${OPERATORS}`;
}

export function getMultiSelectItems(multiSelectValue, selectedOperators, selectedJobs, groupBy, translations) {
    let multiSelectItems;

    if (groupBy === JOB) {
        multiSelectItems = React.useMemo(() => {
            const sortedOperators = [...selectedOperators].sort((a, b) =>
                (a?.onLinkUser?.groupColor || NONE_COLOR).localeCompare(b?.onLinkUser?.groupColor || NONE_COLOR)
            );
            const items = sortedOperators.map(({
                id, firstName, lastName, onLinkUser, onTimeOff
            }) => ({
                icon: (
                    <IconDotFilled
                        primary={onLinkUser?.groupColor}
                        {...ICON_STYLE}
                    />
                ),
                id,
                title: `${firstName} ${lastName}`,
                subtitle: getJobsAssignedToOperator(id, selectedJobs, multiSelectValue),
                ...onTimeOff && {
                    extraContent: (
                        <Stack
                            alignItems='center'
                            direction='row'
                            spacing={1}
                        >
                            <Typography
                                color={NOT_AVAILABLE_COLOR}
                                variant='bodySmall'
                            >
                                {translations.NOT_AVAILABLE}
                            </Typography>
                        </Stack>
                    )
                }
            }));

            const groups = [
                {
                    id: 'unassigned',
                    title: translations.ONLINK_UNASSIGNED,
                    items: []
                },
                {
                    id: 'assigned',
                    title: translations.ASSIGNED,
                    items: []
                }
            ];

            for (const item of items) {
                (item.subtitle ? groups[1].items : groups[0].items).push(item);
            }
            return groups;
        }
        , [selectedOperators, multiSelectValue]);
    } else {
        multiSelectItems = React.useMemo(() => selectedJobs.map(({
            id, title, operatorCount
        }) => ({
            id,
            title,
            subtitle: getOperatorCountForJob(id, operatorCount, multiSelectValue, translations.OPERATORS)
        })), [selectedJobs, multiSelectValue]);
    }
    return multiSelectItems;
}

export function addNewItemToMultiselectValue(setMultiSelectValue, rowId, ids, replace = false) {
    setMultiSelectValue((prev) => {
        return {
            ...prev,
            [rowId]: replace ? ids : [...prev[rowId] || [], ...ids]
        };
    });
}

export function removeValuesFromMultiSelect(setMultiSelectValue, rowId, idsToRemove) {
    setMultiSelectValue((prev) => {
        const values = prev?.[rowId];

        if (!values || !values.length) {
            return prev;
        }
        const filtered = values.filter((id) => !idsToRemove.includes(id));

        if (filtered.length === values.length) {
            return prev;
        }
        return {
            ...prev,
            [rowId]: filtered
        };
    });
}

export function pruneMultiSelectValue(multiSelectValue, rowIds, valueIds) {
    let changed = false;

    const entries = Object.entries(multiSelectValue)
        .filter(([rowId]) => {
            const keep = rowIds.includes(rowId);

            changed ||= !keep;

            return keep;
        })
        .map(([rowId, ids]) => {
            const filteredIds = ids.filter((id) => valueIds.includes(id));

            changed ||= filteredIds.length !== ids.length;

            return [rowId, filteredIds];
        });

    return changed ? Object.fromEntries(entries) : multiSelectValue;
}
/**
 * {
            "equipmentTypeId": "47213b84-0985-11f0-871b-1287bcef2909",
            "name": "Reel Mower (Turf)",
            "models": [
                "1025483",
                "1106971"
            ]
        }
 */
export function getEquipmentTypesModels(job, equipment, capitalizeFirstLetter, translations) {
    const equipmentInJob = job?.equipmentTypes?.reduce((acc, eqType) => {
        acc[eqType.name] = eqType.models || [];
        return acc;
    }, {});
    const result = equipment.reduce((acc, eq) => {
        const equipmentTypeName = eq?.isgType?.name;

        if (eq.status === equipmentStatus.archived) {
            return acc;
        }

        if (!equipmentInJob?.hasOwnProperty(equipmentTypeName)) {
            return acc;
        }

        if (!acc[equipmentTypeName]) {
            acc[equipmentTypeName] = [{
                equipmentId: '',
                equipmentName: capitalizeFirstLetter(translations.NONE)
            }];
        }

        if (equipmentInJob[equipmentTypeName]?.length > 0 && !equipmentInJob[equipmentTypeName].includes(eq.modelId)) {
            return acc;
        }
        acc[equipmentTypeName].push(eq);
        return acc;
    }, {});

    return result;
}
